import { useEffect } from "react";
import { timelineSegments, chapters } from "@/data/chapters";
import { segmentCount, type SegmentFilter } from "./FilterSidebar";

export type SortValue = "default" | "asc" | "desc";

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  segment: SegmentFilter;
  onSegmentChange: (value: SegmentFilter) => void;
  sort: SortValue;
  onSortChange: (value: SortValue) => void;
  onlyFavorites: boolean;
  onToggleFavorites: () => void;
  onReset: () => void;
  resultCount: number;
}

const sortOptions: { value: SortValue; label: string }[] = [
  { value: "default", label: "按剧情顺序" },
  { value: "asc", label: "时长从短到长" },
  { value: "desc", label: "时长从长到短" },
];

const segmentOptions: SegmentFilter[] = ["全部", ...timelineSegments];

export default function FilterDrawer({
  open,
  onClose,
  segment,
  onSegmentChange,
  sort,
  onSortChange,
  onlyFavorites,
  onToggleFavorites,
  onReset,
  resultCount,
}: FilterDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] lg:hidden">
      <button
        type="button"
        aria-label="关闭筛选"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-pointer bg-background-50/70 backdrop-blur-sm"
      />
      <div className="absolute inset-x-0 bottom-0 max-h-[86vh] overflow-y-auto rounded-t-2xl border-t border-background-200 bg-background-100 px-5 pb-6 pt-4">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-background-400" />
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-base text-foreground-950">筛选与排序</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
          >
            <i className="ri-close-line text-base leading-none" />
          </button>
        </div>

        <div className="mt-5">
          <p className="font-label text-[11px] tracking-[0.2em] text-foreground-500">剧情时间线</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {segmentOptions.map((item) => {
              const active = segment === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => onSegmentChange(item)}
                  className={`cursor-pointer rounded-full border px-3.5 py-1.5 font-label text-xs transition-colors ${
                    active
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300 text-foreground-700"
                  }`}
                >
                  {item} · {segmentCount(item)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <p className="font-label text-[11px] tracking-[0.2em] text-foreground-500">排序方式</p>
          <div className="mt-2 flex flex-col gap-2">
            {sortOptions.map((item) => {
              const active = sort === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => onSortChange(item.value)}
                  className={`flex cursor-pointer items-center justify-between rounded-md border px-3.5 py-2.5 font-label text-xs transition-colors ${
                    active
                      ? "border-accent-500/60 bg-accent-500/10 text-accent-400"
                      : "border-background-300 text-foreground-700"
                  }`}
                >
                  {item.label}
                  {active && <i className="ri-check-line text-sm leading-none" />}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={onToggleFavorites}
          className={`mt-5 flex w-full cursor-pointer items-center justify-between rounded-md border px-3.5 py-2.5 font-label text-xs transition-colors ${
            onlyFavorites
              ? "border-accent-500/60 bg-accent-500/10 text-accent-400"
              : "border-background-300 text-foreground-700"
          }`}
        >
          <span className="flex items-center gap-2">
            <i className={`${onlyFavorites ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} text-base leading-none`} />
            只看已收藏章节
          </span>
          {onlyFavorites && <i className="ri-check-line text-sm leading-none" />}
        </button>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={onReset}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-3 font-label text-sm text-foreground-800"
          >
            重置条件
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-3 font-label text-sm font-medium text-background-50"
          >
            查看 {resultCount} 个章节
          </button>
        </div>
        <p className="mt-3 text-center text-[11px] text-foreground-500">
          共 {chapters.length} 个剧情章节可供浏览
        </p>
      </div>
    </div>
  );
}